import { Formik, FormikProps } from 'formik';
import React, { FC, Ref, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { searchHsCode } from '../../../../../actions/customsActions';
import { ICustomsReducer } from '../../../../../interfaces/customs/hsCodes';
import { IStore } from '../../../../../reducers';
import Button from '../../../../base/button';
import FormikFieldWrapper from '../../../../base/forms/formikFieldWrapper';
import { Input } from '../../../../base/forms/input';
import { Column, Columns } from '../../../../base/grid';
import { HsCodeCard, HsCodeCardSkeleton } from './hsCodeCard';
import { hsCodeLookupInitialValues, hsCodeLookupSchema, IHsCodeLookupFormValues } from './hsCodeLookup.schema';

export interface IHsCodeSelection {
	hsCode: string;
	// The item description: the result's friendly text, or the typed description in manual entry.
	description: string;
	hsChapter: string;
	hsHeading: string;
	// True when the user typed the code by hand instead of picking a search result.
	manualEntry: boolean;
}

interface Props {
	recipientCountry: string;
	onSelect: (selection: IHsCodeSelection) => void;
	onCancel?: () => void;
	prefilledSearchTerm?: string;
	// The line's existing code, restored when re-editing a manually entered code.
	prefilledHsCode?: string;
	// Open straight in manual mode (used when editing a line whose code was entered by hand).
	startInManualEntry?: boolean;
	// Ref to the lookup's own Formik, so the outer step-export form can trigger its validation
	// (surfacing the name required error) when the user confirms without selecting a code.
	formikRef?: Ref<FormikProps<IHsCodeLookupFormValues>>;
	// True when the outer form was submitted but this lookup still owes a code, i.e. the missing
	// selection is what blocks the step. Only turns into a message once the description is filled —
	// until then its own required error already tells the user what to do.
	selectionRequired?: boolean;
}

export const HsCodeLookup: FC<Props> = ({
	recipientCountry,
	onSelect,
	onCancel,
	prefilledSearchTerm,
	prefilledHsCode,
	startInManualEntry,
	formikRef,
	selectionRequired,
}) => {
	const { t } = useTranslation('registerShipment');
	const dispatch = useDispatch<any>();

	const { hsCodeSearchResults, hsCodeSearchResultsLoading, hsCodeSearchResultsError } = useSelector<
		IStore,
		ICustomsReducer
	>(store => store.customsReducer);

	const [results, setResults] = useState<typeof hsCodeSearchResults>(null);
	const [manualEntry, setManualEntry] = useState(!!startInManualEntry);

	useEffect(() => {
		if (hsCodeSearchResults && !hsCodeSearchResultsError && !hsCodeSearchResultsLoading) {
			setResults(hsCodeSearchResults);
		}
		if (!hsCodeSearchResults) setResults(null);
	}, [hsCodeSearchResults, hsCodeSearchResultsError, hsCodeSearchResultsLoading]);

	const handleSearch = (values: IHsCodeLookupFormValues) => {
		// Clear previous results so the loading skeleton shows again on every new search.
		setResults(null);
		// The API expects the search term as `name`.
		dispatch(searchHsCode(values.searchTerm, null, recipientCountry));
	};

	// Branches on the submitted values (not the local `manualEntry` state) so it's always in sync
	// with whatever Formik just validated.
	const handleSubmit = (values: IHsCodeLookupFormValues) => {
		if (values.manualEntry) {
			// Manual entry has no chapter/heading titles from the API.
			onSelect({
				hsCode: values.hsCode,
				description: values.searchTerm,
				hsChapter: '',
				hsHeading: '',
				manualEntry: true,
			});
			return;
		}
		handleSearch(values);
	};

	return (
		<Wrapper>
			<Formik
				innerRef={formikRef}
				initialValues={{
					...hsCodeLookupInitialValues,
					searchTerm: prefilledSearchTerm || '',
					hsCode: prefilledHsCode || '',
					manualEntry: !!startInManualEntry,
				}}
				validationSchema={hsCodeLookupSchema}
				onSubmit={handleSubmit}>
				{({ values, setFieldValue, setFieldTouched, submitForm }) => {
					// Switching modes must sync both the local `manualEntry` state (used for
					// rendering) and the Formik field (read by the yup schema's `when('manualEntry', ...)`).
					const enterManualEntry = () => {
						setManualEntry(true);
						setFieldValue('manualEntry', true);
						// Search results are meaningless in manual mode.
						setResults(null);
					};
					const backToSearch = () => {
						setManualEntry(false);
						setFieldValue('manualEntry', false);
					};

					const searchTermField = (
						<Column tablet={manualEntry ? 1 / 2 : undefined} marginBottom={0}>
							<FormikFieldWrapper
								label={`${t(
									manualEntry
										? 'step-export.hsCodeLookup.nameLabel'
										: 'step-export.hsCodeLookup.searchLabel'
								)} *`}
								name="searchTerm"
								smallLabel
								languageComponent="registerShipment"
								reserveErrorSpace>
								<Input
									id="searchTerm"
									name="searchTerm"
									value={values.searchTerm}
									// The 255 characters is a hard limit from SAP/ITMATT but the 300 chars is an arbitrary number
									maxLength={manualEntry ? 255 : 300}
									autoComplete="off"
									onChange={e => setFieldValue('searchTerm', e.target.value)}
									onBlur={() => setFieldTouched('searchTerm', true)}
									placeholder={t(
										manualEntry
											? 'step-export.hsCodeLookup.namePlaceholder'
											: 'step-export.hsCodeLookup.searchPlaceholder'
									)}
								/>
							</FormikFieldWrapper>
						</Column>
					);

					return (
						<div
							onKeyDown={e => {
								if (e.key === 'Enter') {
									e.preventDefault();
									submitForm();
								}
							}}>
							<Title>
								{t(
									manualEntry
										? 'step-export.hsCodeLookup.manualTitle'
										: 'step-export.hsCodeLookup.title'
								)}
							</Title>
							{!manualEntry && hsCodeSearchResultsError && (
								<ErrorMessage>{t(hsCodeSearchResultsError)}</ErrorMessage>
							)}
							{/* startInManualEntry means the panel was opened to re-edit a hand-entered code:
								the user may press "Til baka í leit" and must be able to get back to manual
								without having to make a search fail first. */}
							{!manualEntry && (hsCodeSearchResultsError || startInManualEntry) && (
								<ManualEntryButtonWrapper>
									<Button type="button" onClick={enterManualEntry}>
										{t('step-export.hsCodeLookup.manualEntry')}
									</Button>
								</ManualEntryButtonWrapper>
							)}
							<Columns>
								{manualEntry && (
									<Column tablet={1 / 2} marginBottom={0}>
										<FormikFieldWrapper
											label={`${t('step-export.hsCodeLookup.hsCodeLabel')} *`}
											name="hsCode"
											smallLabel
											languageComponent="registerShipment"
											reserveErrorSpace>
											<Input
												id="hsCode"
												name="hsCode"
												value={values.hsCode}
												inputMode="numeric"
												maxLength={6}
												autoComplete="off"
												// A HS subheading is exactly 6 digits, so strip anything else as the user types
												// (lets them paste a dotted code like "6205.20" and get "620520").
												onChange={e =>
													setFieldValue(
														'hsCode',
														e.target.value.replace(/\D/g, '').slice(0, 6)
													)
												}
												onBlur={() => setFieldTouched('hsCode', true)}
												placeholder={t('step-export.hsCodeLookup.hsCodePlaceholder')}
											/>
										</FormikFieldWrapper>
									</Column>
								)}
								{searchTermField}
							</Columns>
							<ButtonRow>
								{onCancel && (
									<Button type="button" onClick={onCancel}>
										{t('step-export.hsCodeLookup.cancel')}
									</Button>
								)}
								{manualEntry && (
									<Button type="button" onClick={backToSearch}>
										{t('step-export.hsCodeLookup.backToSearch')}
									</Button>
								)}
								<Button type="button" primary onClick={submitForm}>
									{t(
										manualEntry
											? 'step-export.hsCodeLookup.continue'
											: 'step-export.hsCodeLookup.search'
									)}
								</Button>
							</ButtonRow>

							{!manualEntry && (
								<ResultsSection>
									{selectionRequired && !!values.searchTerm.trim() && (
										// data-error-message lets useScrollToFirstFormikError scroll here: the
										// blocking fields (contents.0.*) have no rendered input to scroll to while
										// the lookup is open, so this message is the only thing to point at.
										<ErrorMessage data-error-message>
											{t('step-export.hsCodeLookup.selectionRequired')}
										</ErrorMessage>
									)}
									{hsCodeSearchResultsLoading && !results && (
										<ResultsGrid>
											{[...Array(3)].map((_, i) => (
												<HsCodeCardSkeleton key={i} />
											))}
										</ResultsGrid>
									)}
									{results && (
										<>
											<ResultsTitle>{t('step-export.hsCodeLookup.resultsTitle')}</ResultsTitle>
											{results.length === 0 && <p>{t('step-export.hsCodeLookup.noResults')}</p>}
											<ResultsGrid>
												{results.map(hsCodeItem => {
													const alternate = hsCodeItem.subheadingAlternate;
													const frags = alternate.fragments;
													// fragments[2] is always the fragment with the 6-digit code "XXXX.XX"
													// (guaranteed >=3 fragments by customsActions.ts filter)
													const formattedHsCode = frags[2].code.replace(/\D/g, '');
													const friendly =
														alternate.description?.friendly || frags[2].description;
													return (
														<HsCodeCard
															key={alternate.code}
															hsCode={formattedHsCode}
															title={friendly}
															onClick={() =>
																onSelect({
																	hsCode: formattedHsCode,
																	description: friendly,
																	hsChapter: frags[0].description,
																	hsHeading: frags[1].description,
																	manualEntry: false,
																})
															}
														/>
													);
												})}
											</ResultsGrid>
										</>
									)}
								</ResultsSection>
							)}
						</div>
					);
				}}
			</Formik>
		</Wrapper>
	);
};

export default HsCodeLookup;

// The outline is a shadow ring (no border) so the focus ring doesn't shift anything
const Wrapper = styled.div`
	border-radius: 8px;
	box-shadow: 0 0 0 3px ${({ theme }) => theme.colorsV2.grey5};
	padding: 2rem;

	&:focus-within {
		box-shadow: 0 0 0 4px ${({ theme }) => theme.colorsV2.mainPink}4d;
	}
`;

const Title = styled.p`
	margin: 0 0 1.5rem;
	font-size: 1.8rem;
	font-family: ${({ theme }) => theme.fonts.SharpSlab};
	font-weight: 700;
	color: ${({ theme }) => theme.colors.black};
`;

const ErrorMessage = styled.p`
	color: ${({ theme }) => theme.colors.primary};
	margin: 0 0 1.5rem;
	font-size: 1.5rem;
`;

const ManualEntryButtonWrapper = styled.div`
	margin: -0.5rem 0 1.5rem;
`;

const ButtonRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	margin-top: 1rem;
`;

const ResultsSection = styled.div`
	margin-top: 2rem;
`;

const ResultsTitle = styled.p`
	font-size: 1.7rem;
	font-family: ${({ theme }) => theme.fonts.SharpSlab};
	font-weight: 700;
	color: ${({ theme }) => theme.colors.black};
	margin: 0 0 1rem;
`;

const ResultsGrid = styled.div`
	display: grid;
	gap: 1rem;
	max-height: 40rem;
	overflow-y: auto;
`;
